import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UpdateMembershipTypeCguDto,
  UserMembershipTypeDto,
} from '../../../models/user-membership/dto/user-membership.dto';
import { useApiClient } from '../../../apiClient';
import { GET_USER_MEMBERSHIP_TYPES_QUERY_KEY } from './useGetUserMembershipTypes';

/**
 * POST /organizations/:orgId/membership-types/:typeId/cgu — set or clear the
 * per-membership-type CGU override.
 *
 * Pass `cguText: null` to clear (the type falls back to the org CGU).
 * cguVersion is monotonic on the type — set/clear/re-set all bump it. Existing
 * subscriber snapshots are never affected; only future purchases see the new
 * resolved CGU.
 */
export const useUpdateMembershipTypeCgu = (
  organizationId: string,
  membershipTypeId: string,
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateMembershipTypeCgu', membershipTypeId],
    mutationFn: (body: UpdateMembershipTypeCguDto) =>
      apiClient.fetchApi<UserMembershipTypeDto, UpdateMembershipTypeCguDto>({
        method: 'POST',
        endpoint: `organizations/${organizationId}/membership-types/${membershipTypeId}/cgu`,
        body,
      }),
    onSuccess: (updated) => {
      // Surgically update the cached membership type list so the override flag
      // (cguVersion bump) is reflected without a refetch.
      queryClient.setQueryData<UserMembershipTypeDto[]>(
        GET_USER_MEMBERSHIP_TYPES_QUERY_KEY(organizationId),
        (previous) => {
          if (!previous) return [updated];
          const idx = previous.findIndex((t) => t.id === updated.id);
          if (idx === -1) return [...previous, updated];
          const next = [...previous];
          next[idx] = updated;
          return next;
        },
      );
      // Bust the GET /cgu cache for this specific type so the editor refetches
      // the resolved text on next open.
      queryClient.invalidateQueries({
        queryKey: ['cgu', organizationId, membershipTypeId],
      });
    },
  });
};
